import { computed, ref } from "vue";
import { useStore } from 'vuex';

export const DEFAULT_CONECTA_SCOPE = "SrcServerCognitoConecta/ConectaApiScope";

export function useExternalCredentials() {
    const store = useStore();

    const spinner = ref(true);
    const isLoading = ref(false);
    const rowBusy = ref(null);
    const adminsLoading = ref(false);
    const alertTable = ref({ state: false, message: "", color: "success" });

    const ALERT_TIMEOUT = 5000;
    const SUCCESS_ALERT_TIMEOUT = 3000;

    // Modal state
    const showForm = ref(false);
    const editingCredential = ref(null);

    let alertTimer = null;
    const showAlert = (message, color = "success", timeout = ALERT_TIMEOUT) => {
        alertTable.value = { state: true, message, color };
        if (alertTimer) clearTimeout(alertTimer);
        alertTimer = setTimeout(() => {
            alertTable.value.state = false;
            alertTable.value.message = "";
        }, timeout);
    };

    const apiMessage = (error, fallback) => {
        const rd = error?.response?.data;
        const msg = rd?.error || rd?.message || rd?.errorMessage || rd?.data;
        return typeof msg === 'string' && msg ? msg : fallback;
    };

    const handleError = (error, fallback = "Ha ocurrido un error") => {
        console.error('ExternalCredentials error:', error?.response?.status || '', apiMessage(error, fallback));
        showAlert(apiMessage(error, fallback), "danger", 7000);
    };

    const credentials = computed(() => store.state.appExternalCredentials?.credentials || []);
    const admins = computed(() => store.state.appExternalCredentials?.admins || []);

    const fetchCredentials = async () => {
        spinner.value = true;
        try {
            await store.dispatch('appExternalCredentials/fetchCredentials');
        } catch (error) {
            handleError(error, "Error al cargar las credenciales.");
        } finally {
            spinner.value = false;
        }
    };

    const fetchAdmins = async (filters = {}) => {
        adminsLoading.value = true;
        try {
            await store.dispatch('appExternalCredentials/fetchAdmins', filters);
        } catch (error) {
            console.error('Error al cargar admins destino:', error);
        } finally {
            adminsLoading.value = false;
        }
    };

    // Búsqueda server-side por correo (cubre admins más allá de la primera página)
    const searchAdmins = (query) => fetchAdmins(query ? { email: query } : {});

    const openCreate = () => {
        editingCredential.value = null;
        showForm.value = true;
    };

    const openEdit = (cred) => {
        editingCredential.value = { ...cred };
        showForm.value = true;
    };

    const closeForm = () => {
        showForm.value = false;
        editingCredential.value = null;
    };

    const saveCredential = async (payload) => {
        const id = editingCredential.value?.id;
        isLoading.value = true;
        try {
            if (id) {
                await store.dispatch('appExternalCredentials/updateCredential', { id, payload });
                showAlert("Credencial actualizada correctamente", "success", SUCCESS_ALERT_TIMEOUT);
            } else {
                await store.dispatch('appExternalCredentials/createCredential', payload);
                showAlert("Credencial creada correctamente", "success", SUCCESS_ALERT_TIMEOUT);
            }
            closeForm();
            await fetchCredentials();
        } catch (error) {
            const fallback = error?.response?.status === 422
                ? "Ya existe una credencial para ese partner"
                : "Error al guardar la credencial.";
            handleError(error, fallback);
        } finally {
            isLoading.value = false;
        }
    };

    const deleteCredential = async (cred) => {
        const ok = window.confirm(
            `¿Eliminar la credencial "${cred.partner_name}"?\n\n` +
            `Los inmuebles ya importados NO se eliminan; solo se elimina la integración.`
        );
        if (!ok) return;
        rowBusy.value = cred.id;
        try {
            await store.dispatch('appExternalCredentials/deleteCredential', cred.id);
            showAlert("Credencial eliminada", "success", SUCCESS_ALERT_TIMEOUT);
            await fetchCredentials();
        } catch (error) {
            handleError(error, "Error al eliminar la credencial.");
        } finally {
            rowBusy.value = null;
        }
    };

    const testCredential = async (cred) => {
        rowBusy.value = cred.id;
        try {
            const data = await store.dispatch('appExternalCredentials/testCredential', cred.id);
            const total = data?.total_external ?? 0;
            showAlert(`Conexión OK, ${total} inmuebles en el partner`, "success", SUCCESS_ALERT_TIMEOUT);
        } catch (error) {
            handleError(error, "No se pudo conectar con el partner.");
        } finally {
            rowBusy.value = null;
        }
    };

    const syncCredential = async (cred) => {
        rowBusy.value = cred.id;
        try {
            await store.dispatch('appExternalCredentials/syncCredential', cred.id);
            showAlert("Sync encolado", "success", SUCCESS_ALERT_TIMEOUT);
        } catch (error) {
            handleError(error, "Error al encolar la sincronización.");
        } finally {
            rowBusy.value = null;
        }
    };

    const toggleActive = async (cred) => {
        const next = !cred.active;
        rowBusy.value = cred.id;
        try {
            await store.dispatch('appExternalCredentials/updateCredential', {
                id: cred.id,
                payload: { active: next }
            });
            showAlert(next ? "Credencial activada" : "Credencial desactivada", "success", SUCCESS_ALERT_TIMEOUT);
            await fetchCredentials();
        } catch (error) {
            handleError(error, "Error al cambiar el estado.");
        } finally {
            rowBusy.value = null;
        }
    };

    return {
        // state
        spinner,
        isLoading,
        rowBusy,
        adminsLoading,
        alertTable,
        showForm,
        editingCredential,

        // data
        credentials,
        admins,

        // actions
        fetchCredentials,
        fetchAdmins,
        searchAdmins,
        openCreate,
        openEdit,
        closeForm,
        saveCredential,
        deleteCredential,
        testCredential,
        syncCredential,
        toggleActive,
    };
}
